// ============================================================================
// The command center's arithmetic (item 701, TCSD-0412). Pure functions over
// what the bridge returns: which items need Ben, in what order, which projects
// hold which steps, what is running, and the morning message. No fetching here.
//
// ⛔ THE PRIORITY RULE IS NOT A SECOND RULE. It is scripts/build-room/
// order-board.mjs — money moving wrong › broken for someone › blocks the
// cutover › decision owed › quality › cosmetic, then oldest first. That file
// cannot be imported into the browser (it also shells out to `gh`), so the
// tier list is repeated here and derive.test.ts imports the script itself and
// fails the build the moment the two differ in any label, name or order.
// ============================================================================
// Types only: the smoke suite imports this file in Node, where a value import from
// the bridge without its extension cannot be resolved.
import type { BoardChild, BoardItem, BoardRun, Touches } from '../../../supabase/functions/build-room-bridge/handler';

export type { BoardChild, BoardItem, BoardRun, Touches };

/** Locked to TIERS in scripts/build-room/order-board.mjs by derive.test.ts. */
export const TIERS = [
  { label: 'priority:money-wrong', name: 'Money is moving wrong' },
  { label: 'priority:broken-for-user', name: 'Broken for someone using it' },
  { label: 'priority:blocks-cutover', name: 'Blocks the punch cutover' },
  { label: 'priority:decision-owed', name: 'A decision is owed' },
  { label: 'priority:quality', name: 'Quality and debt' },
  { label: 'priority:cosmetic', name: 'Cosmetic' },
] as const;

export function tierOf(item: { labels: readonly string[] }): number {
  const i = TIERS.findIndex((t) => item.labels.includes(t.label));
  return i === -1 ? TIERS.length : i;
}

export function tierName(item: { labels: readonly string[] }): string {
  return TIERS[tierOf(item)]?.name ?? 'Not prioritised yet';
}

/**
 * Same comparison as orderBoard() in order-board.mjs: tier, then — when the touches
 * counts are given (item #703, TCSD-0423) — more touches first, then oldest first.
 * Without counts it is tier then oldest, as before (parked and Projects keep that).
 */
export function orderBoard<T extends { number?: number; labels: readonly string[]; created_at: string }>(
  items: readonly T[],
  touches?: ReadonlyMap<number, number>,
): T[] {
  const n = (i: T) => (touches && i.number !== undefined ? (touches.get(i.number) ?? 0) : 0);
  return [...items].sort((a, b) => {
    const t = tierOf(a) - tierOf(b);
    if (t !== 0) return t;
    const k = n(b) - n(a);
    if (k !== 0) return k;
    return Date.parse(a.created_at) - Date.parse(b.created_at);
  });
}

// ----- Needs you ------------------------------------------------------------------

/**
 * "What needs you", as the retired board picture defined it: the item's STAGE
 * says it is waiting on a person — a decision with its write-up ready, or a
 * change ready to merge. Ben, 2026-10-06 (item 701, decision 3: A).
 *
 * ⛔ NOT priority:decision-owed. That label is a RANK (fourth of six in the
 * order), not a stage: on 2026-10-06 it sat on 25 items still waiting to be
 * looked into, with no write-up and three of them not Ben's. Counting it put 43
 * on this tab where 18 were ready.
 */
export function isNeedsYou(labels: readonly string[]): boolean {
  return (
    labels.includes('stage:needs-decision') ||
    labels.includes('stage:ready-to-merge') ||
    // Item #827: the write-up's own finding is that nothing is left to decide; only Ben closes it.
    labels.includes('stage:ready-to-close')
  );
}

/** The bots and the command center, as the intake check names them (intake-check.mjs). */
export const PIPELINE_PART = 'Build Room';

/** The parts of the app an item's body names on its "Part(s) of the app:" line. */
export function partsOf(body: string | null | undefined): string[] {
  const m = /^\s*(?:[-*]\s+)?\**\s*parts? of the app\s*\**\s*:\s*\**\s*(.+)$/im.exec(String(body ?? '').replace(/\r/g, ''));
  if (!m) return [];
  return m[1]
    .replace(/\*\*|`/g, '')
    .split(/[,;·]/)
    .map((x) => x.trim().replace(/\.$/, ''))
    .filter(Boolean);
}

/** About the Build Room itself: the bots, their rulebook, the command center. */
export const isUpkeep = (i: Pick<BoardItem, 'body'>) => partsOf(i.body).some((p) => p.toLowerCase() === PIPELINE_PART.toLowerCase());

/** Needs you's four piles (item #827, Ben 9 Oct, D4 option C). */
export interface NeedsYouPiles {
  /** Checked, every check green: one press merges. */
  merge: BoardItem[];
  /** Business rules, money, spend, security, what people will see. */
  calls: BoardItem[];
  /** How to fix the bots and their rulebook: Part of the app is the Build Room. */
  upkeep: BoardItem[];
  /** Nothing left to decide, by the write-up's own finding: Close it or Keep it open. */
  toClose: BoardItem[];
}

export function pilesOf(items: readonly BoardItem[]): NeedsYouPiles {
  const merge = items.filter((i) => i.labels.includes('stage:ready-to-merge'));
  const toClose = items.filter((i) => i.labels.includes('stage:ready-to-close'));
  const rest = items.filter((i) => !merge.includes(i) && !toClose.includes(i));
  return { merge, calls: rest.filter((i) => !isUpkeep(i)), upkeep: rest.filter(isUpkeep), toClose };
}

// ----- Touches, counted (item #703) ------------------------------------------------
// The bridge reads each item's Touches note; this counts them. order-board.mjs uses
// the same functions, so the board page and the command center never disagree.

/**
 * Who touches whom, read both ways: an item's neighbours are every item its note
 * names and every item whose note names it. Only items in the list count.
 */
export function touchNeighbours(items: ReadonlyArray<{ number: number; touches: Touches | null }>): Map<number, Set<number>> {
  const nb = new Map<number, Set<number>>(items.map((i) => [i.number, new Set<number>()]));
  for (const i of items) {
    if (!i.touches) continue;
    for (const l of [...i.touches.settles, ...i.touches.waits_on, ...i.touches.shares]) {
      if (l.number === i.number || !nb.has(l.number)) continue;
      nb.get(i.number)!.add(l.number);
      nb.get(l.number)!.add(i.number);
    }
  }
  return nb;
}

/** A note's parts split by the catalog: the names it has (counted) and the names it does not (listed). */
export function partsKnown(touches: Touches | null, catalogNames: ReadonlySet<string>): { known: string[]; unknown: string[] } {
  const named = touches?.parts ?? [];
  return { known: named.filter((p) => catalogNames.has(p)), unknown: named.filter((p) => !catalogNames.has(p)) };
}

/** `touches N` on every card: the distinct open items linked to it either way. */
export function touchesCounts(items: ReadonlyArray<{ number: number; touches: Touches | null }>): Map<number, number> {
  return new Map([...touchNeighbours(items)].map(([n, s]) => [n, s.size]));
}

/** Ben's order (decision 8 A, TCSD-0423): priority, then more touches, then oldest. */
export function needsYou(items: readonly BoardItem[]): BoardItem[] {
  return orderBoard(
    items.filter((i) => isNeedsYou(i.labels)),
    touchesCounts(items),
  );
}

/**
 * How many places the touches lifted each Needs-you item above the order before
 * them (priority, then oldest) — the demo's "▲ 2 from today". Only rises are kept.
 */
export function movedUp(ranked: readonly BoardItem[]): Map<number, number> {
  const before = new Map(orderBoard(ranked).map((i, k) => [i.number, k]));
  const out = new Map<number, number>();
  ranked.forEach((i, k) => {
    const was = before.get(i.number) ?? k;
    if (was > k) out.set(i.number, was - k);
  });
  return out;
}

// ----- Pinned on top, parked at the bottom (item 702, decisions 6 and 10) ------------

export const isParked = (labels: readonly string[]) => labels.includes('stage:parked');

/**
 * A pin is GitHub's "assigned to" (decision 6). The key owner's login comes from
 * whoami; before that is known, anyone assigned counts — nothing else on the board
 * assigns (measured 7 Oct: no item used the field before the center).
 */
export function isPinned(item: Pick<BoardItem, 'assignees'>, login?: string | null): boolean {
  return login ? item.assignees.includes(login) : item.assignees.length > 0;
}

const newestPinFirst = <T extends Pick<BoardItem, 'pinned_at'>>(a: T, b: T) =>
  Date.parse(b.pinned_at ?? '') - Date.parse(a.pinned_at ?? '');

export interface NeedsYouSections {
  /** Needs-you items Ben pinned, newest pin first. */
  pinned: BoardItem[];
  /** The rest of Needs you, in the board's order (unchanged rule). */
  ranked: BoardItem[];
  /** Every open parked item, priority then oldest: at the bottom, never hidden (decision 10). */
  parked: BoardItem[];
  /** The ranked items sorted into the four piles, each in the board's order. */
  piles: NeedsYouPiles;
  /** Sent back by the checker, or stopped by the Builder: the fifth pile (item #1013). Needs the runs. */
  sentBack: SentBack[];
}

export function needsYouSections(items: readonly BoardItem[], login?: string | null, runs: readonly BoardRun[] = []): NeedsYouSections {
  const needs = needsYou(items);
  const ranked = needs.filter((i) => !isPinned(i, login));
  return {
    pinned: needs.filter((i) => isPinned(i, login)).sort(newestPinFirst),
    ranked,
    parked: orderBoard(items.filter((i) => isParked(i.labels))),
    piles: pilesOf(ranked),
    sentBack: sentBack(items, runs),
  };
}

/** The tab's count: what needs you, the sent-back pile included. Parked items are shown, never counted. */
export const needsYouCount = (s: NeedsYouSections) => s.pinned.length + s.ranked.length + s.sentBack.length;

/** Money moving wrong, broken for someone, or blocking the cutover: the first three tiers. */
export const isUrgentTier = (i: { labels: readonly string[] }) => tierOf(i) <= 2;

/** What a step with nothing for Ben is doing, in plain words. */
export function whereItIs(labels: readonly string[], state: 'open' | 'closed' = 'open'): string {
  if (state === 'closed') return 'Done.';
  if (isParked(labels)) return 'Parked. Nothing is working on it.';
  if (labels.includes('stage:intake')) return 'Just filed, not looked at yet.';
  if (labels.includes('stage:needs-recon')) return 'Waiting for recon to write it up.';
  if (labels.includes('stage:needs-design')) return 'Being designed.';
  if (labels.includes('stage:approved')) return 'Approved, waiting to be built.';
  if (labels.includes('stage:building')) return 'Being built.';
  if (labels.includes('stage:needs-verify')) return 'Being checked.';
  if (labels.includes('stage:ready-to-merge')) return 'Ready to merge.';
  if (labels.includes('stage:ready-to-close')) return 'Ready to close: nothing is left to decide.';
  if (labels.includes('stage:needs-info')) return 'Waiting on information the bots cannot read; the write-up says what is missing.';
  if (labels.includes('stage:needs-decision')) return 'Waiting for your decision.';
  return 'On the board with no stage yet.';
}

/** Calendar days on the reader's clock: filed last night reads "1 day old", not "today". */
export function ageWords(iso: string, now: Date = new Date()): string {
  const day = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((day(now) - day(new Date(iso))) / 86_400_000);
  if (days <= 0) return 'today';
  return `${days} day${days === 1 ? '' : 's'} old`;
}

// ----- Projects -------------------------------------------------------------------

export type ChildStage = 'needs you' | 'parked' | 'bot working' | 'waiting' | 'done';

// Stages where the item sits with the build process rather than with a person.
const BOT_STAGES = [
  'stage:intake',
  'stage:needs-recon',
  'stage:needs-info',
  'stage:needs-design',
  'stage:building',
  'stage:needs-verify',
];

export function childStage(child: { state: 'open' | 'closed'; labels: readonly string[] }): ChildStage {
  if (child.state === 'closed') return 'done';
  if (isNeedsYou(child.labels)) return 'needs you';
  if (child.labels.includes('stage:parked')) return 'parked';
  if (child.labels.some((l) => BOT_STAGES.includes(l))) return 'bot working';
  return 'waiting';
}

export interface Project {
  item: BoardItem;
  children: Array<BoardChild & { stage: ChildStage }>;
  closed: number;
  total: number;
  /** closed over total, as a whole percentage. */
  percent: number;
}

/** Every open item that has sub-items, with its sub-items, in priority order. */
export function projects(items: readonly BoardItem[], login?: string | null): Project[] {
  const all = orderBoard(items.filter((i) => i.children_total > 0));
  // Pinned projects on top, newest pin first (decision 6), then the board's order.
  const ordered = [...all.filter((i) => isPinned(i, login)).sort(newestPinFirst), ...all.filter((i) => !isPinned(i, login))];
  return ordered.map((item) => ({
    item,
    children: item.children.map((c) => ({ ...c, stage: childStage(c) })),
    closed: item.children_closed,
    total: item.children_total,
    percent: item.children_total ? Math.round((item.children_closed / item.children_total) * 100) : 0,
  }));
}

// ----- Running now ----------------------------------------------------------------

export interface LaneEntry {
  key: string;
  title: string;
  detail: string;
  /** The item behind the row, which opens its card in the command center (item #827). Null for a bare bot run. */
  number: number | null;
}

export interface Lanes {
  recon: LaneEntry[];
  design: LaneEntry[];
  /** Approved and waiting for a run: oldest approval first (item #855, decision 1 option 1). */
  ready: LaneEntry[];
  build: LaneEntry[];
}

export function runWords(run: Pick<BoardRun, 'status' | 'conclusion'>): string {
  if (run.status === 'queued' || run.status === 'waiting' || run.status === 'pending') return 'waiting to start';
  if (run.status === 'in_progress') return 'running';
  switch (run.conclusion) {
    case 'success':
      return 'finished';
    case 'failure':
      return 'failed';
    case 'cancelled':
      return 'cancelled';
    case 'timed_out':
      return 'ran out of time';
    default:
      return run.conclusion ? run.conclusion.replace(/_/g, ' ') : 'finished';
  }
}

const isLive = (r: BoardRun) => r.status !== 'completed';
const ASSESSOR = 'Build Room — Assessor';
const BUILD_BOTS = ['Build Room — Builder', 'Build Room — Verifier'];

/**
 * Four columns. Recon: Assessor runs in progress, plus items still waiting on
 * recon that an Assessor run has picked up. Design: items marked needs-design.
 * Ready to run: approved items waiting for a run, oldest first (item #855).
 * Build & check: only work really happening (item #855, Ben 28 Oct) — items being
 * built or checked, with how long each has sat there and the checker's last word,
 * plus Builder and Verifier runs still going. A finished or failed run is on the
 * item, not here; a sent-back item is in Needs you, not here (item #1013).
 */
export function lanes(items: readonly BoardItem[], runs: readonly BoardRun[], now: Date = new Date()): Lanes {
  const titles = new Map(items.map((i) => [i.title, i]));
  const numbers = new Map(items.map((i) => [i.number, i]));
  // A run started from a label carries the item's title; one started by the
  // command center or the recon lanes is named "… item N" (item #737 run).
  const byTitle = {
    get: (t: string) => {
      const m = /\bitem (\d+)$/.exec(t);
      return titles.get(t) ?? (m ? numbers.get(Number(m[1])) : undefined);
    },
  };
  const counted = runs.filter((r) => r.conclusion !== 'skipped');

  const recon = new Map<string, LaneEntry>();
  for (const r of counted.filter((x) => x.workflow === ASSESSOR)) {
    const item = byTitle.get(r.title);
    const stillWaiting = item?.labels.includes('stage:needs-recon') ?? false;
    if (!isLive(r) && !stillWaiting) continue;
    const key = item ? `item-${item.number}` : `run-${r.id}`;
    if (recon.has(key)) continue; // runs arrive newest first; keep the latest
    recon.set(key, { key, title: item?.title ?? r.title, detail: `recon ${runWords(r)}`, number: item?.number ?? null });
  }
  // A data question the bots cannot read stays with them (item #827), shown here.
  for (const i of orderBoard(items.filter((x) => x.labels.includes('stage:needs-info')))) {
    const key = `item-${i.number}`;
    if (!recon.has(key)) recon.set(key, { key, title: i.title, detail: 'needs info the bots cannot read', number: i.number });
  }

  const design = orderBoard(items.filter((i) => i.labels.includes('stage:needs-design'))).map((i) => ({
    key: `item-${i.number}`,
    title: i.title,
    detail: 'being worked out',
    number: i.number,
  }));

  const ready: LaneEntry[] = readyToRun(items, runs).map((i) => ({
    key: `item-${i.number}`,
    title: i.title,
    detail: i.dispatch ? `prompt written · approved ${fmtIso(i.approval?.created_at ?? i.created_at)}` : i.approval?.route === 'builder' ? `Builder route · approved ${fmtIso(i.approval.created_at)}` : i.approval?.route === 'cowork' ? `Cowork · approved ${fmtIso(i.approval.created_at)}` : i.approval ? `no route recorded · approved ${fmtIso(i.approval.created_at)}` : 'approved (older than the last 15 comments)',
    number: i.number,
  }));

  const back = new Set(sentBack(items, runs).map((s) => s.item.number));
  const build = new Map<string, LaneEntry>();
  for (const i of orderBoard(items.filter((x) => x.labels.includes('stage:building') || x.labels.includes('stage:needs-verify')))) {
    if (back.has(i.number)) continue; // sent back: in Needs you, not "being built"
    const live = liveRunsOn(i, runs);
    const builder = live.find((r) => r.workflow === BUILDER);
    const checker = live.find((r) => r.workflow === VERIFIER);
    const last = i.checker.last;
    const days = daysSince(last?.created_at ?? i.updated_at, now);
    const sat = `${days === 0 ? 'today' : `${days} day${days === 1 ? '' : 's'}`}`;
    const word = last ? `checker last said ${last.verdict}${last.findings[0] ? `: ${last.findings[0]}` : ''}` : 'the checker has not looked yet';
    const detail = builder
      ? last?.verdict === 'not ready'
        ? `builder fixing the checker's findings · ${runWords(builder)}`
        : `builder ${runWords(builder)}`
      : checker
        ? `checker ${runWords(checker)}`
        : i.labels.includes('stage:building')
          ? `being built · ${sat === 'today' ? 'moved today' : `quiet for ${sat}`} · ${word}`
          : `being checked · ${sat === 'today' ? 'moved today' : `quiet for ${sat}`} · ${word}`;
    build.set(`item-${i.number}`, { key: `item-${i.number}`, title: i.title, detail, number: i.number });
  }
  for (const r of counted.filter((x) => BUILD_BOTS.includes(x.workflow) && isLive(x))) {
    const item = byTitle.get(r.title);
    const key = item ? `item-${item.number}` : `run-${r.id}`;
    if (build.has(key)) continue;
    const who = r.workflow === 'Build Room — Builder' ? 'builder' : 'checker';
    build.set(key, { key, title: item?.title ?? r.title, detail: `${who} ${runWords(r)}`, number: item?.number ?? null });
  }

  return { recon: [...recon.values()], design, ready, build: [...build.values()] };
}

// ----- Sent back by the checker (item #1013, Ben 28 Oct, option A) ------------------

const BUILDER = 'Build Room — Builder';
const VERIFIER = 'Build Room — Verifier';

/** The bot runs on an item that are still going: running, queued, waiting. */
export function liveRunsOn(item: Pick<BoardItem, 'number' | 'title'>, runs: readonly BoardRun[], workflows: readonly string[] = [BUILDER, VERIFIER]): BoardRun[] {
  const named = new RegExp(`\\bitem ${item.number}$`);
  return runs.filter((r) => workflows.includes(r.workflow) && isLive(r) && (r.title === item.title || named.test(r.title)));
}

export type SentBackKind = 'checker' | 'builder';

export interface SentBack {
  item: BoardItem;
  /** The checker sent it back, or the Builder stopped without pushing its work. */
  kind: SentBackKind;
  /** Nothing in the work to fix: the only finding is a check that did not run. No button. */
  checksOnly: boolean;
  /** How many times the checker has looked. */
  passes: number;
  /** When the checker last said not ready, or when the Builder's note landed. */
  at: string;
  /** The findings in plain words (the checker's), or the Builder's note. */
  findings: string[];
  runUrl: string | null;
  /** How many times Ben has already sent it back to the Builder. */
  sendBacks: number;
}

/**
 * Items the checker sent back and nothing is working on, and items the Builder
 * stopped on without pushing. Both come to Ben. A sent-back item with a Builder
 * or Verifier run still going is being fixed, and stays in Running now instead.
 */
export function sentBack(items: readonly BoardItem[], runs: readonly BoardRun[]): SentBack[] {
  const out: SentBack[] = [];
  for (const i of orderBoard(items)) {
    if (isProject(i) || liveRunsOn(i, runs).length > 0) continue;
    const last = i.checker?.last ?? null;
    if (i.labels.includes('stage:building') && last && last.verdict === 'not ready') {
      out.push({ item: i, kind: 'checker', checksOnly: last.checks_only, passes: i.checker.passes, at: last.created_at, findings: last.findings, runUrl: null, sendBacks: i.send_backs ?? 0 });
      continue;
    }
    const note = i.builder_note ?? null;
    if (i.labels.includes('stage:approved') && note && Date.parse(note.created_at) >= Date.parse(i.approval?.created_at ?? '0')) {
      out.push({ item: i, kind: 'builder', checksOnly: false, passes: i.checker?.passes ?? 0, at: note.created_at, findings: ['The Builder finished without pushing its work, so nothing was opened.'], runUrl: note.run_url, sendBacks: i.send_backs ?? 0 });
    }
  }
  return out;
}

/** After two send-backs, a session is offered first (the write-up's fifth acceptance step). */
export const SESSION_FIRST_AFTER = 2;

// ----- Ready to run (item #855, Ben 28 Oct, decision 1 option 1) --------------------

/** Approved, not a project, not on the sent-back pile, nothing building it: oldest approval first. */
export function readyToRun(items: readonly BoardItem[], runs: readonly BoardRun[]): BoardItem[] {
  const back = new Set(sentBack(items, runs).map((s) => s.item.number));
  return items
    .filter((i) => i.labels.includes('stage:approved') && !isProject(i) && !back.has(i.number) && liveRunsOn(i, runs, [BUILDER]).length === 0)
    .sort((a, b) => Date.parse(a.approval?.created_at ?? a.created_at) - Date.parse(b.approval?.created_at ?? b.created_at) || a.number - b.number);
}

const ROUTE_WORDS: Record<string, string> = {
  cowork: 'Route: Cowork — a prompt Ben runs.',
  builder: 'Route: Builder — the Builder builds it and opens a pull request.',
};

/**
 * The run line, built from the item itself (never a second copy of the job): the
 * item, what Ben approved and when, where the job and its proof are, the route, and
 * the one thing a run must do first. A fuller prompt a session posted ("## Dispatch")
 * is shown instead.
 */
export function runLine(item: BoardItem): { text: string; from: 'item' | 'dispatch' } {
  if (item.dispatch?.body) return { text: item.dispatch.body, from: 'dispatch' };
  const a = item.approval;
  const approved = a
    ? `Ben approved ${a.words || 'it'} on ${fmtIso(a.created_at)}.`
    : 'Ben approved it (the approval comment is older than the last 15 comments; read the item).';
  const route = a?.route ? ROUTE_WORDS[a.route] : 'Route: not recorded on the approval — Cowork unless the item says otherwise.';
  return {
    from: 'item',
    text: [
      `Build item #${item.number} in tcs-app: ${item.title}.`,
      `${approved} Read the item and every comment first — the newest write-up's "If approved" is the job, and its acceptance test is the proof.`,
      route,
      'Move the item to being built when you start.',
    ].join('\n'),
  };
}

function fmtIso(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Whole days since, on the reader's clock, for "quiet for N days" on a lane row. */
export function daysSince(iso: string, now: Date = new Date()): number {
  return Math.max(0, Math.round((now.getTime() - Date.parse(iso)) / 86_400_000));
}

// ----- The morning message --------------------------------------------------------

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * The prompt's shape, minus one clause. "Because it affects the most projects"
 * was to be counted from items sharing a label — but every label on the board is
 * a stage, type, priority, owner, risk or push-back, which nearly every item
 * shares, so that count would rank nothing. It says what is true instead: why
 * the first item is first under the rule.
 */
export function morningMessage(
  needs: readonly BoardItem[],
  l: Lanes | { unreadable: string },
  parked: readonly BoardItem[] = [],
  touches: ReadonlyMap<number, number> = new Map(),
): string {
  // The approved design's words (6 Oct correction): how many need you, and how
  // many of those have a write-up ready, i.e. can be decided from today.
  const ready = needs.filter((n) => n.write_up).length;
  const reach = (i: BoardItem) => touches.get(i.number) ?? 0;
  // The 7 Oct demo's sentence: how many each touch three or more open items.
  const big = needs.filter((n) => reach(n) >= 3).length;
  const head =
    needs.length === 0
      ? 'Nothing needs you'
      : `${plural(needs.length, 'thing needs', 'things need')} you — ${ready === 0 ? 'none' : ready} with a write-up ready to decide`;
  const bigSaid = big ? ` ${big} of them each touch three or more open items.` : '';
  const first = needs[0];
  // The touches decided it when an older item of the same priority sits below it.
  const byTouches =
    first &&
    reach(first) > 0 &&
    needs.some((n) => n !== first && tierOf(n) === tierOf(first) && Date.parse(n.created_at) < Date.parse(first.created_at) && reach(n) < reach(first));
  const why = !first
    ? ''
    : byTouches
      ? ` First is “${first.title}” — it sits highest under the priority rule (${tierName(first).toLowerCase()}) because it touches ${plural(reach(first), 'open item', 'open items')}, more than the older ones beside it.`
      : ` First is “${first.title}” — it sits highest under the priority rule (${tierName(first).toLowerCase()}), oldest first.`;
  // The runs could not be read: say what is known, and say what is not, rather
  // than dropping the count that WAS read or printing zeros nobody measured.
  // Decision 10: parked stays in view. K counts the first three priorities.
  const hot = parked.filter(isUrgentTier).length;
  const parkedSaid = parked.length
    ? ` ${parked.length} parked at the bottom, ${hot} of them marked money, broken or blocking the cutover.`
    : '';
  if ('unreadable' in l) return `Morning. ${head}.${bigSaid}${why}${parkedSaid} ${l.unreadable}`;
  const running =
    `${plural(l.recon.length, 'recon', 'recons')} and ${plural(l.design.length, 'design', 'designs')} ` +
    `${l.recon.length + l.design.length === 1 ? 'is' : 'are'} running, ` +
    `${plural(l.build.length, 'build', 'builds')} in check.`;
  return `Morning. ${head}.${bigSaid} ${running}${why}${parkedSaid}`;
}

// ----- Feedback ------------------------------------------------------------------

/** Statuses that are finished. Everything else is open. */
export const CLOSED_FEEDBACK = ['completed', 'wont_fix', 'duplicate'] as const;

const FEEDBACK_WORDS: Record<string, string> = {
  new: 'just reported',
  triaged: 'looked at, not started',
  in_progress: 'being worked on',
};

export function feedbackWords(status: string): string {
  return FEEDBACK_WORDS[status] ?? status.replace(/_/g, ' ');
}

// ----- Feedback ↔ board (item 702, decision 7) ----------------------------------------

/** feedback-intake.mjs's rule: an item belongs to a report when its body names the id. */
export function mentions(body: string, feedbackId: string): boolean {
  return new RegExp(`\\b${feedbackId}\\b`).test(body);
}

/** The open board items that belong to a report. */
export function openItemsFor(feedbackId: string, items: readonly BoardItem[]): BoardItem[] {
  return items.filter((i) => mentions(i.body, feedbackId));
}

/**
 * The draft resolution offered when a report's item finished another way: the
 * finished item's last comment, without the command center's own prefix, for Ben
 * to edit or send. Never sent without his press.
 */
export function draftResolution(lastComment: string | null | undefined, itemTitle: string): string {
  const t = (lastComment ?? '').replace(/^From the command center:\s*/i, '').trim();
  if (!t) return `Fixed in "${itemTitle}".`;
  return t.length > 1200 ? `${t.slice(0, 1200).trimEnd()}…` : t;
}

// ----- Overlaps and the Map (item #703; TCSD-0419, TCSD-0423) -------------------------
//
// Everything below is counted from the Touches notes the bridge reads (one per open
// item, written by the Assessor). Nothing is guessed from a title: an item with no
// note is said to have none, and never counted as touching nothing.

/** A project has steps under it. Projects are not named in notes and carry none. */
export const isProject = (i: Pick<BoardItem, 'children_total'>) => i.children_total > 0;

/** The open items a note may name: open, and not a project. */
export const noteItems = (items: readonly BoardItem[]) => items.filter((i) => !isProject(i));

/** The stage pill on an Overlaps or Map row, in the 7 Oct demo's words. */
const PILL_WORDS: Record<string, string> = {
  'stage:intake': 'just filed',
  'stage:needs-recon': 'waiting for recon',
  'stage:needs-design': 'being designed',
  'stage:approved': 'approved, not built yet',
  'stage:building': 'being built',
  'stage:needs-verify': 'being checked',
  'stage:needs-info': 'needs info',
  'stage:parked': 'parked',
};
export function stagePill(labels: readonly string[]): string {
  if (isNeedsYou(labels)) return 'needs you';
  const s = labels.find((l) => l.startsWith('stage:'));
  return s ? (PILL_WORDS[s] ?? s.slice(6).replace(/-/g, ' ')) : 'no stage yet';
}

export type LinkKind = 'settles' | 'waits' | 'shares';

/** One line of an item's Overlaps block: what it does to another open item, and why. */
export interface ItemLink {
  kind: LinkKind;
  number: number;
  reason: string;
  /** Whose note said it: this item's own, or the other item's (read the other way). */
  from: 'mine' | 'theirs';
}

/**
 * Every link that touches an item, read both ways: its own note's lines as written,
 * and every other note's line about it turned round ("B settles A" is "A waits on B").
 * A disagreement shows both sides. One line per kind and item.
 */
export function linksOf(n: number, items: readonly BoardItem[]): ItemLink[] {
  const out: ItemLink[] = [];
  const add = (l: ItemLink) => {
    if (!out.some((x) => x.kind === l.kind && x.number === l.number)) out.push(l);
  };
  const me = items.find((i) => i.number === n);
  const present = new Set(items.map((i) => i.number));
  if (me?.touches) {
    for (const l of me.touches.settles) if (present.has(l.number)) add({ kind: 'settles', number: l.number, reason: l.reason, from: 'mine' });
    for (const l of me.touches.waits_on) if (present.has(l.number)) add({ kind: 'waits', number: l.number, reason: l.reason, from: 'mine' });
    for (const l of me.touches.shares) if (present.has(l.number)) add({ kind: 'shares', number: l.number, reason: l.reason, from: 'mine' });
  }
  for (const o of items) {
    if (o.number === n || !o.touches) continue;
    for (const l of o.touches.settles) if (l.number === n) add({ kind: 'waits', number: o.number, reason: l.reason, from: 'theirs' });
    for (const l of o.touches.waits_on) if (l.number === n) add({ kind: 'settles', number: o.number, reason: l.reason, from: 'theirs' });
    for (const l of o.touches.shares) if (l.number === n) add({ kind: 'shares', number: o.number, reason: l.reason, from: 'theirs' });
  }
  return out;
}

export interface OverlapGroup {
  /** The group's items, the one to decide first leading, then priority, then oldest. */
  items: BoardItem[];
  /** The item whose answer settles the most others in the group; null → build together. */
  first: BoardItem | null;
  /** How many of the others in the group the first one settles. */
  settles: number;
  /** The parts of the app the group's notes share, most-named first. */
  parts: string[];
}

export interface OverlapsView {
  groups: OverlapGroup[];
  /** Items with a note that touch nothing else that's open. */
  alone: BoardItem[];
  /** Open items that are not projects and have no Touches note yet. */
  noNote: BoardItem[];
}

const oldestFirst = (a: BoardItem, b: BoardItem) => Date.parse(a.created_at) - Date.parse(b.created_at) || a.number - b.number;

/**
 * Groups = items joined by any link, two or more per group. Decide first = the
 * item whose answer settles the most others in the group; ties by priority, then
 * oldest (Ben, 8 Oct, on #703). No "settles" inside a group → build together.
 */
export function overlaps(items: readonly BoardItem[]): OverlapsView {
  const pool = noteItems(items);
  const byNum = new Map(pool.map((i) => [i.number, i]));
  const adj = new Map<number, Set<number>>(pool.map((i) => [i.number, new Set<number>()]));
  for (const i of pool) for (const l of linksOf(i.number, pool)) adj.get(i.number)!.add(l.number);

  const seen = new Set<number>();
  const groups: OverlapGroup[] = [];
  for (const start of [...pool].sort(oldestFirst)) {
    if (seen.has(start.number) || adj.get(start.number)!.size === 0) continue;
    const comp: number[] = [];
    const stack = [start.number];
    seen.add(start.number);
    while (stack.length) {
      const n = stack.pop()!;
      comp.push(n);
      for (const m of adj.get(n)!) if (!seen.has(m)) {
        seen.add(m);
        stack.push(m);
      }
    }
    const inGroup = new Set(comp);
    const settles = (n: number) => linksOf(n, pool).filter((l) => l.kind === 'settles' && inGroup.has(l.number)).length;
    const ranked = comp
      .map((n) => byNum.get(n)!)
      .sort((a, b) => settles(b.number) - settles(a.number) || tierOf(a) - tierOf(b) || oldestFirst(a, b));
    const top = ranked[0];
    const k = settles(top.number);
    const partCount = new Map<string, number>();
    for (const i of ranked) for (const p of i.touches?.parts ?? []) partCount.set(p, (partCount.get(p) ?? 0) + 1);
    groups.push({
      items: k > 0 ? ranked : [...ranked].sort((a, b) => tierOf(a) - tierOf(b) || oldestFirst(a, b)),
      first: k > 0 ? top : null,
      settles: k,
      parts: [...partCount].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).map(([p]) => p),
    });
  }
  // Groups that hold more open work first; then the one whose first item ranks higher.
  groups.sort((g, h) => h.items.length - g.items.length || tierOf(g.items[0]) - tierOf(h.items[0]) || oldestFirst(g.items[0], h.items[0]));
  return {
    groups,
    alone: pool.filter((i) => i.touches && adj.get(i.number)!.size === 0).sort(oldestFirst),
    noNote: pool.filter((i) => !i.touches).sort(oldestFirst),
  };
}

/**
 * A group's heading. The notes carry no name for a group, so it is named by the
 * parts of the app its notes share (two at most) — or, when they name none, by
 * its first item.
 */
export function groupHeading(g: OverlapGroup): string {
  if (g.parts.length) return g.parts.slice(0, 2).join(' · ');
  return (g.first ?? g.items[0]).title;
}

/** Why the group is together, in the demo's two shapes, from the notes' own reasons. */
export function groupWhy(g: OverlapGroup, items: readonly BoardItem[]): string {
  const others = g.items.length - 1;
  if (g.first) {
    const all = g.settles === others;
    return `Its answer settles ${all ? (others === 1 ? 'the other one' : `the other ${others}`) : `${g.settles} of the other ${others}`} in this group.`;
  }
  const inGroup = new Set(g.items.map((i) => i.number));
  const reasons = g.items
    .flatMap((i) => linksOf(i.number, items).filter((l) => l.kind === 'shares' && inGroup.has(l.number) && l.from === 'mine').map((l) => l.reason))
    .filter((r, k, a) => a.indexOf(r) === k);
  const shared = reasons.length ? ` They share records: ${reasons.slice(0, 2).join('; ')}.` : '';
  return `None of them settles another.${shared} Build them in one run.`;
}

/**
 * The five areas, in order-board.mjs's order and words (Ben's grouping,
 * 2026-09-21). Locked to AREAS in scripts/build-room/order-board.mjs by derive.test.ts.
 */
export const AREAS = ['Sales', 'Operations', 'People', 'Finance', 'App'] as const;

export interface CatalogPart {
  name: string;
  business_area: string | null;
  public_page: boolean;
}

/**
 * Every part of the app: `name:`, `business_area:` and `public_page:` from
 * catalog/*.yml. The catalog reader shells out to the file system, so it cannot run
 * in the browser; this copy is locked to it by derive.test.ts, which fails the
 * moment a catalog file is added, renamed or moved to another area.
 */
export const CATALOG_PARTS: readonly CatalogPart[] = [
  { name: 'Bids', business_area: 'Sales', public_page: false },
  { name: 'Contracts', business_area: 'Sales', public_page: false },
  { name: 'Account information collection (archived)', business_area: 'Operations', public_page: false },
  { name: 'Account Management', business_area: 'Operations', public_page: false },
  { name: 'EVSiQ', business_area: 'Operations', public_page: false },
  { name: 'Fleet and assets', business_area: 'Operations', public_page: true },
  { name: 'Map', business_area: 'Operations', public_page: false },
  { name: 'My Route', business_area: 'Operations', public_page: false },
  { name: 'My Work', business_area: 'Operations', public_page: false },
  { name: 'New accounts', business_area: 'Operations', public_page: false },
  { name: 'Projects and billing communication', business_area: 'Operations', public_page: false },
  { name: 'Record routing', business_area: 'Operations', public_page: false },
  { name: 'Supply', business_area: 'Operations', public_page: false },
  { name: 'Tasks', business_area: 'Operations', public_page: false },
  { name: 'Time and labour', business_area: 'Operations', public_page: true },
  { name: 'E-Sign', business_area: 'People', public_page: true },
  { name: 'Hiring', business_area: 'People', public_page: false },
  { name: 'HR', business_area: 'People', public_page: false },
  { name: 'Onboarding', business_area: 'People', public_page: false },
  { name: 'Partner application', business_area: 'People', public_page: true },
  { name: 'Separations', business_area: 'People', public_page: false },
  { name: 'Sourcing', business_area: 'People', public_page: false },
  { name: 'Subcontractor onboarding portal', business_area: 'People', public_page: true },
  { name: 'Subcontractor replacement', business_area: 'People', public_page: false },
  { name: 'Subcontractors', business_area: 'People', public_page: false },
  { name: 'Annual increases', business_area: 'Finance', public_page: false },
  { name: 'Incentives', business_area: 'Finance', public_page: false },
  { name: 'Invoicing', business_area: 'Finance', public_page: false },
  { name: 'Money rules', business_area: 'Finance', public_page: false },
  { name: 'Recent money changes', business_area: 'Finance', public_page: false },
  { name: 'Admin', business_area: 'App', public_page: false },
  { name: 'Ask AI', business_area: 'App', public_page: false },
  { name: 'Dashboard', business_area: 'App', public_page: false },
  { name: 'Design preview', business_area: 'App', public_page: true },
  { name: 'Help chat', business_area: 'App', public_page: false },
  { name: 'Home', business_area: 'App', public_page: false },
  { name: 'The app shell', business_area: 'App', public_page: false },
];

export interface MapPart {
  name: string;
  /** Open items whose Touches note names this part, priority then oldest. */
  items: BoardItem[];
}

export interface MapArea {
  area: string;
  parts: MapPart[];
  /** Distinct open items naming any part of the area. */
  open: number;
  publicPages: number;
}

export interface MapView {
  areas: MapArea[];
  /** Parts whose area is none of the five, listed after them. */
  noArea: MapPart[];
  /** Names written in notes that the catalog does not have, with the items that wrote them. */
  unknown: Array<{ name: string; items: BoardItem[] }>;
  noNote: BoardItem[];
  /** (item, named part) pairs counted — the Map's total. */
  pairs: number;
}

export function mapView(items: readonly BoardItem[], catalog: readonly CatalogPart[] = CATALOG_PARTS): MapView {
  const pool = noteItems(items);
  const names = new Set(catalog.map((c) => c.name));
  const byPart = new Map<string, BoardItem[]>(catalog.map((c) => [c.name, []]));
  const unknown = new Map<string, BoardItem[]>();
  let pairs = 0;
  for (const i of pool) {
    const { known, unknown: odd } = partsKnown(i.touches, names);
    for (const p of known) {
      byPart.get(p)!.push(i);
      pairs += 1;
    }
    for (const p of odd) unknown.set(p, [...(unknown.get(p) ?? []), i]);
  }
  const part = (c: CatalogPart): MapPart => ({ name: c.name, items: orderBoard(byPart.get(c.name)!) });
  const areas = AREAS.map((area) => {
    const parts = catalog
      .filter((c) => c.business_area === area)
      .map(part)
      .sort((a, b) => b.items.length - a.items.length || a.name.localeCompare(b.name));
    const open = new Set(parts.flatMap((p) => p.items.map((i) => i.number))).size;
    return { area, parts, open, publicPages: catalog.filter((c) => c.business_area === area && c.public_page).length };
  });
  return {
    areas,
    noArea: catalog.filter((c) => !(AREAS as readonly string[]).includes(c.business_area ?? '')).map(part),
    unknown: [...unknown].map(([name, its]) => ({ name, items: its })),
    noNote: pool.filter((i) => !i.touches).sort(oldestFirst),
    pairs,
  };
}
